// Page weight: the coastal spot page's heaviest client props, sent as what the client components
// read. The server component packs them (coastalClientPayload) and UnifiedCoastal unpacks the
// season heatmaps once per payload, so the page looks and behaves exactly as before.
//
// Measured on /conditions/nazare (29 September 2026): 1.69 MB of HTML, 1.14 MB of it the
// UnifiedCoastal props; these trims take those props to 0.39 MB (heatmaps 499 KB to 28 KB, hourly
// scores 312 KB to 33 KB):
// - Season heatmaps (about 500 KB): 365 day objects for each of up to 12 sports, each carrying its
//   date, a label and a source string, the location's crowd level (the same for every sport) and
//   an undefined snow depth confidence that the RSC payload writes out as "$undefined". Packed,
//   the dates become one start date, the labels, sources and crowd levels become one character per
//   day into small tables, and the crowd column is sent once for every sport that shares it.
// - Hourly scores for yesterday, today and tomorrow (about 310 KB): a named SUP profile's stored
//   hour carries its full safety manifest, and other stored hours a compatibility safety
//   explanation. The page's hourly strip, window summary, scene and visualiser read only the hour,
//   score and daylight (and a ski hour's range and withheld state), so the rest stays on the
//   server, where the ground-truth review windows still read the full rows.
// Pure, and type imports only, so UnifiedCoastal can import the unpacking half.
import type { HeatmapDataset } from "@/app/_components/conditions/SeasonHeatmap";
import type { CrowdLevel } from "@/lib/conditions/crowd-prediction";
import type { StoredHourlyScore } from "@/lib/conditions/daily-obs-types";
import type { HeatmapData, HeatmapDay, HeatmapStats } from "@/lib/conditions/season-heatmap";
import type { StoredHourlyRangeFields } from "@/lib/conditions/stored-precomputed-scores";

const DAY_MS = 86_400_000;
/** One base-36 character per day indexes a table, so a table holds at most 36 values. */
const MAX_TABLE_SIZE = 36;
const DAY_KEYS = new Set(["date", "score", "label", "source", "crowdLevel", "snowDepthConfidence"]);

/** A heatmap packed into columns over the shared day range. */
type PackedHeatmapSet = {
  key: string;
  label: string;
  stats: HeatmapStats;
  /** One score per day, in date order. */
  scores: (number | null)[];
  labels: (string | null)[];
  /** One base-36 index into `labels` per day. */
  labelIndex: string;
  sources: HeatmapDay["source"][];
  /** One base-36 index into `sources` per day. */
  sourceIndex: string;
};

/** A heatmap that does not fit the shared columns, sent as its days (without undefined keys). */
type VerbatimHeatmapSet = { key: string; label: string; data: HeatmapData };

export type CompactHeatmapDatasets = {
  /** First date of the day range every packed set covers, one day per index. */
  start: string;
  days: number;
  crowdLevels: CrowdLevel[];
  /** The shared crowd level per day: one base-36 index into `crowdLevels` per day. */
  crowd: string;
  /** In display order. */
  sets: (PackedHeatmapSet | VerbatimHeatmapSet)[];
};

function isoDay(startMs: number, index: number): string {
  return new Date(startMs + index * DAY_MS).toISOString().slice(0, 10);
}

/** Values and one index character per value, or null when the table would outgrow one character. */
function tabulate<T>(values: readonly T[]): { table: T[]; index: string } | null {
  const table: T[] = [];
  const positions = new Map<T, number>();
  let index = "";
  for (const value of values) {
    let position = positions.get(value);
    if (position === undefined) {
      position = table.length;
      if (position >= MAX_TABLE_SIZE) return null;
      table.push(value);
      positions.set(value, position);
    }
    index += position.toString(36);
  }
  return { table, index };
}

function lookup<T>(table: readonly T[], index: string, day: number): T {
  return table[parseInt(index[day], 36)];
}

/** True when the days run one per date from `start`, carry only the known keys and no snow depth confidence. */
function fitsColumns(days: readonly HeatmapDay[], startMs: number, count: number): boolean {
  if (days.length !== count) return false;
  return days.every((day, index) =>
    day.date === isoDay(startMs, index) &&
    day.snowDepthConfidence === undefined &&
    Object.keys(day).every((key) => DAY_KEYS.has(key)));
}

function withoutUndefinedKeys(day: HeatmapDay): HeatmapDay {
  const { snowDepthConfidence, ...rest } = day;
  return snowDepthConfidence === undefined ? rest : { ...rest, snowDepthConfidence };
}

function verbatim(dataset: HeatmapDataset): VerbatimHeatmapSet {
  return {
    key: dataset.key,
    label: dataset.label,
    data: { days: dataset.data.days.map(withoutUndefinedKeys), stats: dataset.data.stats },
  };
}

/**
 * Packs the season heatmaps into shared columns. The day range and crowd column come from the
 * first dataset that fits them; a dataset with another range, a snow depth confidence, unknown
 * keys, its own crowd levels or more than 36 distinct labels is sent as its days instead.
 */
export function compactHeatmapDatasets(datasets: readonly HeatmapDataset[]): CompactHeatmapDatasets {
  let shape: { start: string; startMs: number; days: number; crowdLevels: CrowdLevel[]; crowd: string } | null = null;
  for (const dataset of datasets) {
    const days = dataset.data.days;
    const start = days[0]?.date;
    if (start === undefined) continue;
    const startMs = Date.parse(`${start}T00:00:00Z`);
    if (!Number.isFinite(startMs) || !fitsColumns(days, startMs, days.length)) continue;
    const crowd = tabulate(days.map((day) => day.crowdLevel));
    if (!crowd) continue;
    shape = { start, startMs, days: days.length, crowdLevels: crowd.table, crowd: crowd.index };
    break;
  }
  if (!shape) return { start: "", days: 0, crowdLevels: [], crowd: "", sets: datasets.map(verbatim) };
  const { start, startMs, days: count, crowdLevels, crowd } = shape;
  const sharedCrowd = (days: readonly HeatmapDay[]) =>
    days.every((day, index) => day.crowdLevel === lookup(crowdLevels, crowd, index));
  return {
    start,
    days: count,
    crowdLevels,
    crowd,
    sets: datasets.map((dataset) => {
      const days = dataset.data.days;
      if (!fitsColumns(days, startMs, count) || !sharedCrowd(days)) return verbatim(dataset);
      const labels = tabulate(days.map((day) => day.label));
      const sources = tabulate(days.map((day) => day.source));
      if (!labels || !sources) return verbatim(dataset);
      return {
        key: dataset.key,
        label: dataset.label,
        stats: dataset.data.stats,
        scores: days.map((day) => day.score),
        labels: labels.table,
        labelIndex: labels.index,
        sources: sources.table,
        sourceIndex: sources.index,
      };
    }),
  };
}

/** The heatmaps as the season components read them, only for the dataset keys `include` accepts. */
export function expandHeatmapDatasets(
  compact: CompactHeatmapDatasets,
  include: (key: string) => boolean = () => true,
): HeatmapDataset[] {
  const startMs = Date.parse(`${compact.start}T00:00:00Z`);
  return compact.sets.filter((set) => include(set.key)).map((set) => {
    if ("data" in set) return set;
    const days: HeatmapDay[] = set.scores.map((score, index) => ({
      date: isoDay(startMs, index),
      score,
      label: lookup(set.labels, set.labelIndex, index),
      source: lookup(set.sources, set.sourceIndex, index),
      crowdLevel: lookup(compact.crowdLevels, compact.crowd, index),
    }));
    return { key: set.key, label: set.label, data: { days, stats: set.stats } };
  });
}

/**
 * An hourly score as the page's client components read it: the hour, score and daylight, a ski
 * hour's range and withheld state, and the confidence that types a named SUP profile's hour.
 */
function clientHourlyPoint(point: StoredHourlyScore): StoredHourlyScore {
  const range: StoredHourlyRangeFields = point as StoredHourlyScore & StoredHourlyRangeFields;
  const extras: StoredHourlyRangeFields = {
    ...(range.lo === undefined ? {} : { lo: range.lo }),
    ...(range.hi === undefined ? {} : { hi: range.hi }),
    ...(range.st === undefined ? {} : { st: range.st }),
  };
  if ("confidence" in point) {
    return { hour: point.hour, score: point.score, isDay: point.isDay, confidence: point.confidence, ...extras };
  }
  return { hour: point.hour, score: point.score, isDay: point.isDay, ...extras };
}

export function clientHourlyScores(
  scores: Readonly<Record<string, readonly StoredHourlyScore[]>>,
): Record<string, StoredHourlyScore[]> {
  return Object.fromEntries(Object.entries(scores).map(([sport, points]) => [sport, points.map(clientHourlyPoint)]));
}

type CoastalPayloadSource = {
  heatmapDatasets: readonly HeatmapDataset[];
  sportHourlyScores: Readonly<Record<string, readonly StoredHourlyScore[]>>;
  yesterdaySportScores: Readonly<Record<string, readonly StoredHourlyScore[]>>;
  tomorrowSportScores: Readonly<Record<string, readonly StoredHourlyScore[]>>;
};

/** The heavy UnifiedCoastal props, trimmed to what the client reads (see the module header). */
export function coastalClientPayload(data: CoastalPayloadSource): {
  heatmapDatasets: CompactHeatmapDatasets;
  sportHourlyScores: Record<string, StoredHourlyScore[]>;
  yesterdaySportScores: Record<string, StoredHourlyScore[]>;
  tomorrowSportScores: Record<string, StoredHourlyScore[]>;
} {
  return {
    heatmapDatasets: compactHeatmapDatasets(data.heatmapDatasets),
    sportHourlyScores: clientHourlyScores(data.sportHourlyScores),
    yesterdaySportScores: clientHourlyScores(data.yesterdaySportScores),
    tomorrowSportScores: clientHourlyScores(data.tomorrowSportScores),
  };
}
